import { Button } from '@awaze/design-system.components.button'
import { Container } from '@awaze/design-system.components.container'
import { Link } from '@awaze/design-system.components.link'
import { Headline } from '@awaze/design-system.components.text'
import { ContentfulError } from 'contentful/validation/validate-model'
import { HTMLAttributes, useEffect, useRef, useState } from 'react'
import { contentfulLink } from '@/components/Utils/PreviewBanner'
import styled, { keyframes } from 'styled-components'

interface P extends HTMLAttributes<HTMLDivElement> {
  errors: ContentfulError[]
}

const popAnimation = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.5, 0.5);
  }

  90% {
    opacity: 1;
    transform: scale(1.1, 1.1);
  }

  100% {
    opacity: 1;
    transform: scale(1, 1);
}`

const ShowErrorsContainer = styled(Button)`
  background: rgba(255, 105, 180, 0.5);
  position: fixed;
  top: 40px;
  left: 14px;
  z-index: 10;
  animation-duration: 0.3s;
  animation-name: ${popAnimation};
  animation-timing-function: cubic-bezier(0.26, 0.53, 0.74, 1.48);
  &:hover {
    background: hotpink;
  }
`

const ErrorContainer = styled.div<{
  visible: boolean
  fullyVisible: boolean
  maxHeight?: number
}>`
  position: relative;
  background: rgba(255, 105, 180, 0.5);
  border: 3px solid white;
  box-shadow: ${(p) =>
    p.fullyVisible && 'hotpink 0px -2px 4px, hotpink 0px 2px 2px'};
  transition: margin 0.5s, max-height 0.5s ease-out;
  max-height: ${(p) => (p.visible ? p.maxHeight : 0)}px;
  overflow: hidden;
  margin: ${(p) => (p.visible ? '100px 0' : 0)};
`

const HeaderContainer = styled.div`
  padding: 24px 0;
`

const HideButton = styled(Button)`
  position: absolute;
  left: 14px;
  top: 26px;
`

export const ModuleErrors: React.FC<P> = ({ errors, ...domAttributes }) => {
  const [showErrors, setShowErrors] = useState(true)
  const [isAnimatingOut, setIsAnimatingOut] = useState(false)
  const errorsRef = useRef<HTMLDivElement>(null)

  const [actualHeight, setActualHeight] = useState<number>(-1)

  useEffect(() => {
    setActualHeight(errorsRef.current.offsetHeight)
  }, [
    errorsRef.current?.offsetHeight &&
      errorsRef.current?.offsetHeight > actualHeight,
  ])

  return (
    <>
      {!showErrors && (
        <ShowErrorsContainer
          onClick={() => {
            setShowErrors(true)
            requestAnimationFrame(() =>
              errorsRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              }),
            )
          }}
          text={'Show errors'}
          variant="outline"
          colourScheme="reversed"
        />
      )}
      <ErrorContainer
        {...domAttributes}
        ref={errorsRef}
        visible={showErrors && !isAnimatingOut}
        fullyVisible={showErrors}
        maxHeight={actualHeight}
        onTransitionEnd={() => {
          if (isAnimatingOut) {
            setShowErrors(false)
            setIsAnimatingOut(false)
          }
        }}
      >
        <Container>
          <HideButton
            onClick={() => setIsAnimatingOut(true)}
            text={'Hide errors'}
            variant="outline"
            colourScheme="reversed"
          />
          <HeaderContainer>
            <Headline as="h1">Oh no, errors found in contentful</Headline>
          </HeaderContainer>
          <ul style={{ marginTop: 24 }}>
            {errors.map((e) => (
              <li key={e.contentfulId || e.message}>
                <p>
                  {e.contentfulId ? (
                    <Link
                      href={contentfulLink(e.contentfulId)}
                      title="View in contentful"
                      openInNewTab
                    >
                      {e.message}
                    </Link>
                  ) : (
                    e.message
                  )}
                </p>
              </li>
            ))}
          </ul>
        </Container>
      </ErrorContainer>
    </>
  )
}
